import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  formatDateTime,
  getAnnouncements,
  getHouseCounts,
  HOUSES,
  publishAnnouncement,
  type AnnouncementRow,
  type Audience,
} from '../lib/api'
import { Card, Empty, ErrorBox, Spinner, WarnBox } from '../components/bits'
import { ConfirmModal, Pager } from '../components/Modal'

const PER_PAGE = 20

/**
 * Communications — composing and reviewing announcements.
 *
 * An announcement fans out to push and email, so it is effectively irreversible:
 * there is no unsending it. Two consequences shape this screen.
 *
 * First, it shows the **recipient count before you send**, computed from the same
 * audience rules the server uses, so nobody discovers they reached 900 people when
 * they meant to reach one house. Second, publishing goes through a confirm step
 * that names the audience and the number.
 */
export default function Communications() {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<Audience>('all')
  const [house, setHouse] = useState<string>(HOUSES[0])
  const [yearFrom, setYearFrom] = useState('')
  const [yearTo, setYearTo] = useState('')

  const [rows, setRows] = useState<AnnouncementRow[]>([])
  const [page, setPage] = useState(1)
  const [houseCounts, setHouseCounts] = useState<Record<string, number>>({})
  const [approvedTotal, setApprovedTotal] = useState(0)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, houses] = await Promise.all([
        getAnnouncements({ page, perPage: PER_PAGE }),
        getHouseCounts(),
      ])
      setRows(list ?? [])
      const counts: Record<string, number> = {}
      let total = 0
      for (const h of houses ?? []) {
        counts[h.house] = h.member_count
        total += h.member_count
      }
      setHouseCounts(counts)
      setApprovedTotal(total)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Mirrors the server's audience rules so the number shown is the number sent.
   * A year range cannot be counted client-side without the full register, so it
   * is reported honestly as unknown rather than guessed at.
   */
  function estimatedReach(): number | null {
    if (audience === 'all') return approvedTotal
    if (audience === 'house') return houseCounts[house] ?? 0
    return null
  }

  const reach = estimatedReach()

  function validate(): string | null {
    if (title.trim().length < 3) return 'Give the announcement a title.'
    if (body.trim().length < 10) return 'The message body is too short.'
    if (audience === 'passingYearRange') {
      const from = Number(yearFrom)
      const to = Number(yearTo)
      if (!Number.isInteger(from) || !Number.isInteger(to) || from === 0 || to === 0) {
        return 'Enter both years for a passing-year range.'
      }
      if (from < 1957 || to < 1957) return 'Nothing predates 1957.'
      if (from > to) return 'The first year must not be after the last.'
    }
    return null
  }

  async function publish(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const result = await publishAnnouncement({
        title: title.trim(),
        body: body.trim(),
        audience,
        house: audience === 'house' ? house : null,
        yearFrom: audience === 'passingYearRange' ? Number(yearFrom) : null,
        yearTo: audience === 'passingYearRange' ? Number(yearTo) : null,
        publishNow: true,
      })
      setNotice(
        `Published to ${result.recipients} ${
          result.recipients === 1 ? 'Petarian' : 'Petarians'
        }. Push and email delivery is handled by a worker reading the announcement row.`,
      )
      setTitle('')
      setBody('')
      setConfirming(false)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  function audienceLabel(a: AnnouncementRow): string {
    if (a.audience === 'house') return `${a.audience_house} House`
    if (a.audience === 'passingYearRange') {
      return `Passed ${a.audience_year_from}–${a.audience_year_to}`
    }
    return 'Everyone'
  }

  const total = rows[0]?.total ?? 0
  const validationError = validate()

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Communications</div>
        <h1 style={{ marginTop: 8 }}>Announcements</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Sent to verified Petarians by push notification and email. Only approved
          records receive them.
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {notice && (
        <Card style={{ borderColor: '#2f8fd4' }}>
          <div className="row">
            <span className="small">{notice}</span>
            <div className="spacer" />
            <button className="btn btn-secondary btn-sm" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        </Card>
      )}

      <Card>
        <h2>New announcement</h2>

        <div className="stack" style={{ marginTop: 'var(--s-5)', gap: 'var(--s-4)' }}>
          <Field label="Title">
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Annual reunion — 14 March"
              maxLength={160}
            />
          </Field>

          <Field label="Message">
            <textarea
              className="input"
              style={{ height: 130, padding: 'var(--s-3) var(--s-4)', lineHeight: 1.6 }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write it as it will appear on the dashboard and in the email."
            />
          </Field>

          <Field label="Audience">
            <div className="filter-row">
              {(
                [
                  { value: 'all' as Audience, label: 'Everyone' },
                  { value: 'house' as Audience, label: 'One house' },
                  { value: 'passingYearRange' as Audience, label: 'Passing years' },
                ]
              ).map((a) => (
                <button
                  key={a.value}
                  className={`tab ${audience === a.value ? 'active' : ''}`}
                  onClick={() => setAudience(a.value)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </Field>

          {audience === 'house' && (
            <Field label="House">
              <select
                className="input"
                style={{ maxWidth: 260 }}
                value={house}
                onChange={(e) => setHouse(e.target.value)}
              >
                {HOUSES.map((h) => (
                  <option key={h} value={h}>
                    {h}
                    {houseCounts[h] !== undefined ? ` (${houseCounts[h]})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {audience === 'passingYearRange' && (
            <>
              <p className="small muted" style={{ margin: 0 }}>
                A record written before the serving-cadet path was removed has
                no passing year, so a year range never reaches it — use Everyone
                or the house.
              </p>
              <div className="row" style={{ gap: 'var(--s-4)' }}>
                <Field label="From year">
                  <input
                    className="input"
                    style={{ maxWidth: 130 }}
                    inputMode="numeric"
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value.replace(/\D/g, ''))}
                    placeholder="1990"
                    maxLength={4}
                  />
                </Field>
                <Field label="To year">
                  <input
                    className="input"
                    style={{ maxWidth: 130 }}
                    inputMode="numeric"
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value.replace(/\D/g, ''))}
                    placeholder="1999"
                    maxLength={4}
                  />
                </Field>
              </div>
            </>
          )}

          <WarnBox>
            {reach === null
              ? 'The exact recipient count for a year range is confirmed by the server when you publish.'
              : reach === 0
                ? 'This currently reaches nobody — there are no verified records in that audience yet.'
                : `This will reach ${reach} verified ${reach === 1 ? 'Petarian' : 'Petarians'}.`}{' '}
            Announcements cannot be unsent.
          </WarnBox>

          <div className="row">
            <div className="spacer" />
            <button
              className="btn btn-primary"
              onClick={() => setConfirming(true)}
              disabled={busy || validationError !== null}
              title={validationError ?? undefined}
            >
              Publish
            </button>
          </div>

          {validationError && title !== '' && (
            <p className="small muted" style={{ margin: 0 }}>
              {validationError}
            </p>
          )}
        </div>
      </Card>

      <Card style={{ padding: 'var(--s-5)' }}>
        <h2 style={{ padding: '0 var(--s-4) var(--s-4)' }}>Sent</h2>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty
            title="Nothing sent yet"
            body="Announcements you publish appear here with their audience and author."
          />
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Published</th>
                  <th>Title</th>
                  <th>Audience</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>
                      {a.published_at ? (
                        formatDateTime(a.published_at)
                      ) : (
                        <span className="muted">Draft</span>
                      )}
                    </td>
                    <td>
                      <strong style={{ color: 'var(--text)' }}>{a.title}</strong>
                      <br />
                      <span className="small muted">
                        {a.body.length > 110 ? `${a.body.slice(0, 110)}…` : a.body}
                      </span>
                    </td>
                    <td>
                      <span className="pill">{audienceLabel(a)}</span>
                    </td>
                    <td className="small">
                      {a.author_name ?? <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pager
              page={page}
              perPage={PER_PAGE}
              total={total}
              busy={loading}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      {confirming && (
        <ConfirmModal
          title="Publish this announcement?"
          body={
            reach === null
              ? `"${title.trim()}" will be sent to every Petarian who passed between ${yearFrom} and ${yearTo}. This cannot be unsent.`
              : `"${title.trim()}" will be sent to ${reach} verified ${
                  reach === 1 ? 'Petarian' : 'Petarians'
                } by push notification and email. This cannot be unsent.`
          }
          confirmLabel="Publish now"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void publish()}
        />
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="kpi-label">{label}</span>
      <div style={{ marginTop: 6 }}>{children}</div>
    </label>
  )
}
